import React, { useState, useEffect } from "react";
import ReusableTable from "components/Tables/ReusableTable";
import {
    Flex,
    Box,
    Text,
    Badge,
} from "@chakra-ui/react";
import { EllipsisVertical, Pencil, EyeIcon, MessageSquare, Gift } from "lucide-react";
import ViewDriver from "./components/ViewDriver";
import EditDriverDrawer from "./components/EditDriverDrawer";
import ReusableDrawer from "../../../components/Drawers/ReusableDrawer.js";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import { useDispatch, useSelector } from "react-redux";
import { fetchDriversAction } from "../../../state/actions/driverActions";
import { formatDate } from "utils/dateFormatter";
import { useCustomNotification } from "components/Toasts/OpenNotification.js";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useHistory } from "react-router-dom";
import { allocateDriverPlanAction } from "state/actions/planActions";
import AllocateDriverPlanDrawer from "./components/AllocateDriverPlanDrawer";
import StatusBadge, {
    getStatusVariant,
} from "components/Badges/StatusBadge";

const DriverManagement = () => {
    const [isViewDriverDrawerOpen, setViewDriverDrawerOpen] = useState(false);
    const [isEditDriverDrawerOpen, setEditDriverDrawerOpen] = useState(false);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [isAllocateDrawerOpen, setAllocateDrawerOpen] = useState(false);

    const dispatch = useDispatch();
    const showToast = useCustomNotification();
    const history = useHistory();
    const { drivers, loading, error } = useSelector((state) => state.drivers);

    useEffect(() => {
        dispatch(fetchDriversAction());
    }, [dispatch]);

    const handleViewDriver = (driver) => {
        setSelectedDriver(driver);
        setViewDriverDrawerOpen(true);
    };

    const handleCloseViewDriverDrawer = () => {
        setSelectedDriver(null);
        setViewDriverDrawerOpen(false);
    };

    const handleOpenEditDriverDrawer = (driver) => {
        setSelectedDriver(driver);
        setEditDriverDrawerOpen(true);
    };

    const handleCloseEditDriverDrawer = () => {
        setSelectedDriver(null);
        setEditDriverDrawerOpen(false);
    };

    const handleOpenAllocateDrawer = (driver) => {
        setSelectedDriver(driver);
        setAllocateDrawerOpen(true);
    };

    const handleCloseAllocateDrawer = () => {
        setSelectedDriver(null);
        setAllocateDrawerOpen(false);
    };

    const handleAllocateDeliveries = async (payload) => {
        try {
            await dispatch(
                allocateDriverPlanAction(payload)
            );

            showToast({
                type: "success",
                message: "Allocation Successful",
                description:
                    "Driver deliveries allocated successfully.",
            });

            handleCloseAllocateDrawer();
        } catch (error) {
            showToast({
                type: "error",
                message: "Allocation Failed",
                description:
                    error?.message ||
                    "Failed to allocate deliveries.",
            });
        }
    };

    const generateMenuItems = (row) => [
        {
            icon: <EyeIcon size={16} />,
            text: "View Driver",
            onClick: () => handleViewDriver(row.original),
        },
        {
            icon: <MessageSquare size={16} />,
            text: "Chat with Driver",
            onClick: () => {
                history.push(
                    `/admin/communications?userId=${row.original?.user?.user_id}`
                );
            },
        },
        {
            icon: <Pencil size={16} />,
            text: "Update Driver Status",
            onClick: () => handleOpenEditDriverDrawer(row.original),
        },
        {
            icon: <Gift size={16} />,
            text: "Allocate Free Deliveries",
            onClick: () => handleOpenAllocateDrawer(row.original),
        },
    ];

    const statusColorScheme = {
        Approved: "green",
        Pending: "yellow",
        Suspended: "red",
        Rejected: "red",
        Deactivated: "gray",
    };

    const columnsData = [
        {
            Header: "Driver Name",
            accessor: (row) => {
                const firstName = row.user?.first_name || "";
                const lastName = row.user?.last_name || "";
                return `${firstName} ${lastName}`.trim() || "N/A";
            },
        },
        {
            Header: "License Number",
            accessor: (row) => row.driver?.license_number || "N/A",
        },
        {
            Header: "License Expiry Date",
            accessor: (row) => row.driver?.license_expiry_date,
            Cell: ({ value }) => (value ? formatDate(value) : "N/A"),
        },
        {
            Header: "Route Preference",
            accessor: (row) => row.driver?.route_preference || "N/A",
        },
        {
            Header: "Package Preference",
            accessor: (row) => row.driver?.package_preference || "N/A",
        },
       {
    Header: "Status",
    accessor: (row) =>
        row.driver?.status || "Unknown",

    Cell: ({ value }) => (
        <StatusBadge
            label={value}
            variant={getStatusVariant(value)}
        />
    ),
},
        {
            Header: "Created",
            accessor: (row) => row.driver?.created_at,
            Cell: ({ value }) => (value ? formatDate(value) : "N/A"),
        },
        {
            Header: "Actions",
            accessor: "moreOptions",
            Cell: ({ row }) => (
                <ThreeDotMenu
                    icon={<EllipsisVertical size={20} color="grey" />}
                    items={generateMenuItems(row)}
                />
            ),
        },
    ];

    return (
        <Flex flexDirection="column" pt={{ base: "120px", md: "25px" }}>
            <Box p={6}>
                <Flex justifyContent="space-between" alignItems="center">
                    <Text fontSize="md" fontWeight="bold">
                        {loading ? (
                            <Skeleton height="20px" width="100px" />
                        ) : (
                            `All Drivers: ${drivers?.total ?? 0}`
                        )}
                    </Text>
                </Flex>
            </Box>

            <ReusableTable
                columnsData={columnsData}
                tableData={Array.isArray(drivers?.data) ? drivers.data : []}
                isLoading={loading}
            />

            {/* View Driver Drawer */}
            <ReusableDrawer
                isOpen={isViewDriverDrawerOpen}
                onClose={handleCloseViewDriverDrawer}
                headerContent={<h2 style={{ fontSize: "md" }}>View Driver</h2>}
                bodyContent={
                    selectedDriver && (
                        <ViewDriver
                            driver={selectedDriver}
                            onClose={handleCloseViewDriverDrawer}
                        />
                    )
                }
            />

            {/* Edit Driver Drawer */}
            <EditDriverDrawer
                isOpen={isEditDriverDrawerOpen}
                onClose={handleCloseEditDriverDrawer}
                driver={selectedDriver}
            />
            <AllocateDriverPlanDrawer
                isOpen={isAllocateDrawerOpen}
                onClose={handleCloseAllocateDrawer}
                driver={selectedDriver}
                onAllocate={handleAllocateDeliveries}
            />
        </Flex>
    );
};

export default DriverManagement;